/**
 * AIAnalysis Page
 *
 * @description サロンハブAI分析ページ - 世界最高水準UI実装
 *
 * Features:
 * - AI駆動のインテリジェント分析
 * - 予約パターン分析と集客強化提案
 * - CSチーム実行依頼機能
 * - Framer Motionによるアニメーション
 * - WCAG 2.1 AA準拠のアクセシビリティ
 */

import React, { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  TrendingUp,
  ArrowRight,
  Mail,
  BarChart3,
  Users,
  Calendar,
  DollarSign,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface AIInsight {
  id: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  expectedImpact: string;
  confidence: number;
  actionLabel: string;
  icon: React.ReactNode;
  category: 'revenue' | 'efficiency' | 'customer' | 'staff';
  timeToImplement: string;
  actionHandler?: () => void;
}

const AIAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [claudeResponse, setClaudeResponse] = useState<string>('');
  const [expandedInsights, setExpandedInsights] = useState<string[]>([]);

  // AI Insights データ
  const insights: AIInsight[] = useMemo(() => [
    {
      id: '1',
      priority: 'high',
      title: '土曜日14-16時のスタッフ増員',
      description: '過去30日のデータ分析により、土曜日14-16時に予約キャンセル待ちが平均8件発生しています。スタッフを1名増員することで、機会損失を防げます。',
      expectedImpact: '¥45,000/週',
      confidence: 94,
      actionLabel: 'シフト調整画面へ',
      icon: <Users className="w-4 h-4" />,
      category: 'efficiency',
      timeToImplement: '即日実施可能',
      actionHandler: () => console.log('Navigate to shift management'),
    },
    {
      id: '2',
      priority: 'high',
      title: '新規顧客向け限定クーポン発行',
      description: '新規顧客の獲得率が先月比15%低下しています。初回限定20%OFFクーポンの発行により、新規顧客の増加が見込めます。',
      expectedImpact: '¥120,000/月',
      confidence: 88,
      actionLabel: 'クーポン作成',
      icon: <DollarSign className="w-4 h-4" />,
      category: 'revenue',
      timeToImplement: '1営業日',
      actionHandler: () => console.log('Create coupon'),
    },
    {
      id: '3',
      priority: 'medium',
      title: '人気メニューのセット化提案',
      description: 'ヘッドスパとフェイシャルの同時利用率が45%と高いため、セットメニュー化により客単価向上が期待できます。',
      expectedImpact: '¥80,000/月',
      confidence: 76,
      actionLabel: 'メニュー編集',
      icon: <TrendingUp className="w-4 h-4" />,
      category: 'customer',
      timeToImplement: '3営業日',
      actionHandler: () => console.log('Edit menu'),
    },
    {
      id: '4',
      priority: 'low',
      title: '平日午前の稼働率改善',
      description: '平日10-12時の稼働率が35%と低いため、シニア向け特別プランの導入を検討してください。',
      expectedImpact: '¥30,000/月',
      confidence: 65,
      actionLabel: '詳細分析',
      icon: <Clock className="w-4 h-4" />,
      category: 'efficiency',
      timeToImplement: '1週間',
      actionHandler: () => console.log('View detailed analysis'),
    },
  ], []);

  // Claude AI連携ハンドラー（モック実装）
  const handleClaudeInteraction = useCallback((query: string) => {
    // 実際のAI統合時にはここでAPI呼び出し
    const mockResponse = `【${query}の分析結果】

1. 平日14-16時の空き時間帯活用
   → SNS限定クーポンで新規顧客獲得

2. リピート率向上施策
   → 3回目来店で10%割引のロイヤリティプログラム

3. 客単価アップ
   → プレミアムメニューのアップセル提案（施術中）

これらの施策により、3ヶ月で売上20%増が見込めます。`;

    setClaudeResponse(mockResponse);
  }, []);

  // CS実行依頼メール作成
  const handleCSRequest = useCallback(() => {
    const storeName = 'HITOYASUMI';
    const currentPlan = 'Premium';
    const subject = `CS実行依頼：AI分析結果の代行実施（${storeName}）`;
    const body = `
店舗名: ${storeName}
プラン: ${currentPlan}
依頼日時: ${new Date().toLocaleString('ja-JP')}

【AI分析結果】
${claudeResponse}

【実行希望内容】
上記のAI分析結果をもとに、具体的な施策の実行をお願いします。

【希望実施時期】
1週間以内

【その他要望・質問】


※このメールはPremiumプラン利用者からの依頼です
※CS担当者：優先度高で対応をお願いします
    `.trim();

    try {
      window.location.href = `mailto:cs@salonhub.jp?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    } catch (error) {
      alert('メールクライアントを開けませんでした。cs@salonhub.jpまで直接ご連絡ください。');
    }
  }, [claudeResponse]);

  const toggleExpand = (insightId: string) => {
    setExpandedInsights(prev =>
      prev.includes(insightId) ? prev.filter(id => id !== insightId) : [...prev, insightId]
    );
  };

  const getPriorityColor = (priority: AIInsight['priority']) => {
    switch (priority) {
      case 'high':
        return 'bg-destructive text-destructive-foreground';
      case 'medium':
        return 'bg-amber-500 text-white';
      case 'low':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-muted';
    }
  };

  const getPriorityLabel = (priority: AIInsight['priority']) => {
    switch (priority) {
      case 'high':
        return '優先度: 高';
      case 'medium':
        return '優先度: 中';
      case 'low':
        return '優先度: 低';
      default:
        return '';
    }
  };

  const getCategoryColor = (category: AIInsight['category']) => {
    switch (category) {
      case 'revenue':
        return 'bg-green-100';
      case 'efficiency':
        return 'bg-blue-100';
      case 'customer':
        return 'bg-amber-100';
      case 'staff':
        return 'bg-purple-100';
      default:
        return 'bg-muted';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'bg-green-600';
    if (confidence >= 60) return 'bg-amber-600';
    return 'bg-destructive';
  };

  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* ページヘッダー */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8"
      >
        <div className="flex items-center gap-4 mb-2">
          <div className="w-16 h-16 bg-indigo-500 rounded-2xl flex items-center justify-center">
            <Sparkles className="text-white h-8 w-8" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-3xl font-bold">サロンハブ AI分析</h1>
              <Badge className="h-6 text-xs px-3 bg-orange-50 text-orange-700 border-orange-300">
                PREMIUM
              </Badge>
            </div>
            <p className="text-muted-foreground">
              AI駆動のインテリジェント分析で売上最大化
            </p>
          </div>
        </div>
      </motion.div>

      {/* メインコンテンツ */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* 左カラム - AI提案・分析 */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="space-y-6"
        >
          {/* 本日のAI提案 */}
          <Card className="border-orange-200 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-orange-600" />
                本日のAI提案
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-orange-50 border-orange-200">
                <TrendingUp className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-sm text-orange-900">
                  今月の予約パターンから、平日14-16時の集客強化を推奨します
                </AlertDescription>
              </Alert>

              <p className="text-sm text-orange-900">
                💡 <strong>本日の提案:</strong> 常連客への割引キャンペーンで来店頻度UP
              </p>

              <Button
                onClick={() => handleClaudeInteraction('売上を20%上げるには？')}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white min-h-[44px]"
              >
                詳細分析を見る
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          {/* AI分析による改善提案 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <CardTitle className="text-base font-semibold">AI分析による改善提案</CardTitle>
                </div>
              </CardHeader>

              <CardContent>
                <Alert className="mb-6">
                  <Lightbulb className="w-4 h-4" />
                  <AlertDescription>
                    過去30日間のデータを分析し、売上向上のための実行可能なアクションを提案します
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  {insights.map((insight) => (
                    <Card
                      key={insight.id}
                      className={`border-l-4 ${
                        insight.priority === 'high'
                          ? 'border-l-destructive'
                          : insight.priority === 'medium'
                          ? 'border-l-amber-500'
                          : 'border-l-blue-500'
                      } ${expandedInsights.includes(insight.id) ? 'bg-muted/30' : ''}`}
                    >
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`p-1.5 rounded ${getCategoryColor(insight.category)}`}>
                                  {insight.icon}
                                </div>
                                <h3 className="font-semibold text-sm">{insight.title}</h3>
                              </div>

                              <div className="flex flex-wrap gap-2 mb-3">
                                <Badge className={getPriorityColor(insight.priority)}>
                                  {getPriorityLabel(insight.priority)}
                                </Badge>
                                <Badge variant="outline" className="gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  信頼度: {insight.confidence}%
                                </Badge>
                                <Badge variant="outline" className="gap-1">
                                  <Clock className="w-3 h-3" />
                                  {insight.timeToImplement}
                                </Badge>
                              </div>

                              <p
                                className={`text-sm text-muted-foreground ${
                                  expandedInsights.includes(insight.id) ? '' : 'line-clamp-2'
                                }`}
                              >
                                {insight.description}
                              </p>

                              <Collapsible open={expandedInsights.includes(insight.id)}>
                                <CollapsibleContent className="mt-4 pt-4 border-t space-y-4">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-xs text-muted-foreground mb-1">期待効果</p>
                                      <p className="text-xl font-bold text-primary">{insight.expectedImpact}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground mb-1">信頼度スコア</p>
                                      <div className="flex items-center gap-2">
                                        <div className="relative flex-1 h-2 overflow-hidden rounded-full bg-primary/20">
                                          <div
                                            className={`h-full transition-all ${getConfidenceColor(insight.confidence)}`}
                                            style={{ width: `${insight.confidence}%` }}
                                          />
                                        </div>
                                        <span className="text-sm font-bold">{insight.confidence}%</span>
                                      </div>
                                    </div>
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>

                              <div className="flex gap-2 mt-3">
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    if (insight.actionHandler) {
                                      insight.actionHandler();
                                    }
                                  }}
                                >
                                  {insight.actionLabel}
                                </Button>
                                <Collapsible open={expandedInsights.includes(insight.id)}>
                                  <CollapsibleTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => toggleExpand(insight.id)}
                                    >
                                      {expandedInsights.includes(insight.id) ? (
                                        <>
                                          <ChevronUp className="w-4 h-4 mr-1" />
                                          閉じる
                                        </>
                                      ) : (
                                        <>
                                          <ChevronDown className="w-4 h-4 mr-1" />
                                          詳細
                                        </>
                                      )}
                                    </Button>
                                  </CollapsibleTrigger>
                                </Collapsible>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* AI分析結果表示 */}
          {claudeResponse && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="border-green-200 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-green-600" />
                    AI分析結果
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert className="bg-green-50 border-green-200">
                    <AlertDescription className="text-sm text-green-900 whitespace-pre-wrap">
                      <strong>サロンハブ AI:</strong> {claudeResponse}
                    </AlertDescription>
                  </Alert>

                  <Button
                    variant="outline"
                    onClick={handleCSRequest}
                    className="w-full min-h-[44px]"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    CSチームに実行依頼
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </motion.div>

        {/* 右カラム - KPIインサイト */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="space-y-6"
        >
          {/* KPIインサイト */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">KPIインサイト</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <BarChart3 className="h-8 w-8 text-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium">月間売上トレンド</p>
                  <p className="text-xs text-muted-foreground">+8.5% 前月比</p>
                </div>
                <Badge variant="default">好調</Badge>
              </div>

              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <Users className="h-8 w-8 text-blue-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">新規顧客獲得</p>
                  <p className="text-xs text-muted-foreground">12名 今月</p>
                </div>
                <Badge variant="secondary">安定</Badge>
              </div>

              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <Calendar className="h-8 w-8 text-indigo-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">予約稼働率</p>
                  <p className="text-xs text-muted-foreground">82% 今週</p>
                </div>
                <Badge variant="default">好調</Badge>
              </div>

              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <DollarSign className="h-8 w-8 text-green-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">平均客単価</p>
                  <p className="text-xs text-muted-foreground">¥21,356</p>
                </div>
                <Badge variant="secondary">改善余地</Badge>
              </div>
            </CardContent>
          </Card>

          {/* クイックアクション */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">クイックアクション</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start min-h-[44px]"
                onClick={() => navigate('/sales')}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                売上詳細を確認
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start min-h-[44px]"
                onClick={() => navigate('/customers')}
              >
                <Users className="mr-2 h-4 w-4" />
                顧客分析を見る
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start min-h-[44px]"
                onClick={() => navigate('/reservations')}
              >
                <Calendar className="mr-2 h-4 w-4" />
                予約状況を確認
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AIAnalysis;
